/**
 * Payment Method Store - Zustand State Management
 * 
 * Manages payment method state.
 * 
 * @validates Requirements 1-8 - Payment methods management
 */

import { create } from 'zustand';
import {
  PaymentMethod,
  CreatePaymentMethodRequest,
  UpdatePaymentMethodRequest,
  ResetTemplatesResult,
} from '@budgira/shared';
import { paymentMethodService } from '../services/paymentMethod.service';
import i18n from '../i18n';

interface PaymentMethodState {
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchPaymentMethods: () => Promise<void>;
  createPaymentMethod: (data: CreatePaymentMethodRequest) => Promise<PaymentMethod | null>;
  updatePaymentMethod: (id: string, data: UpdatePaymentMethodRequest) => Promise<PaymentMethod | null>;
  deletePaymentMethod: (id: string) => Promise<boolean>;
  resetTemplates: () => Promise<ResetTemplatesResult | null>;
  removeTemplate: () => Promise<number>;
  purgeUnused: () => Promise<number>;
  findPaymentMethodById: (id: string) => PaymentMethod | undefined;
  clearError: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizePaymentMethodError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;
  
  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }
  
  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    'PAYMENT_METHOD_NOT_FOUND': 'paymentMethods.errors.notFound',
    'CANNOT_MODIFY_SYSTEM': 'paymentMethods.errors.cannotModifySystem',
    'DUPLICATE_NAME': 'paymentMethods.errors.duplicateName',
    'HAS_ASSOCIATED_DATA': 'paymentMethods.errors.hasAssociatedData',
    'HAS_ASSOCIATED_OPERATIONS': 'paymentMethods.errors.hasAssociatedOperations',
    'HAS_ASSOCIATED_SCHEDULERS': 'paymentMethods.errors.hasAssociatedSchedulers',
    'TEMPLATE_NOT_FOUND': 'paymentMethods.errors.templateNotFound',
    'TEMPLATE_ALREADY_ADDED': 'paymentMethods.errors.templateAlreadyAdded',
  };
  
  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }
  
  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('paymentMethods.errors.generic'));
}

export const usePaymentMethodStore = create<PaymentMethodState>((set, get) => ({
  paymentMethods: [],
  isLoading: false,
  error: null,

  fetchPaymentMethods: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.list();
      if (response.success && response.data) {
        // Sort alphabetically by name
        const sorted = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
        set({ paymentMethods: sorted, isLoading: false });
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
    }
  },

  createPaymentMethod: async (data: CreatePaymentMethodRequest): Promise<PaymentMethod | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.create(data);
      if (response.success && response.data) {
        // Refresh the list
        await get().fetchPaymentMethods();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updatePaymentMethod: async (id: string, data: UpdatePaymentMethodRequest): Promise<PaymentMethod | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.update(id, data);
      if (response.success && response.data) {
        // Refresh the list
        await get().fetchPaymentMethods();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deletePaymentMethod: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.delete(id);
      if (response.success) {
        // Refresh the list
        await get().fetchPaymentMethods();
        return true;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return false;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  resetTemplates: async (): Promise<ResetTemplatesResult | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.resetTemplates();
      if (response.success && response.data) {
        // Refresh the list
        await get().fetchPaymentMethods();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  removeTemplate: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.removeTemplate();
      if (response.success && response.data) {
        await get().fetchPaymentMethods();
        return response.data.count;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  purgeUnused: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.purgeUnused();
      if (response.success && response.data) {
        await get().fetchPaymentMethods();
        return response.data.count;
      } else {
        set({ error: response.error?.message || String(i18n.t('paymentMethods.errors.generic')), isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = localizePaymentMethodError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  findPaymentMethodById: (id: string): PaymentMethod | undefined => {
    return get().paymentMethods.find((pm) => pm.id === id);
  },

  clearError: () => set({ error: null }),
}));

export default usePaymentMethodStore;
